import React, { useState, useEffect, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { MapLayout } from '@vibemap/ui-components';
import apiFetch from '@wordpress/api-fetch';

const MapComponent = ({ attributes }) => {
  const { height = 400, showBoundary = true, forceMobileView = false } = attributes;
  const [places, setPlaces] = useState([]);
  const [boundary, setBoundary] = useState(null);
  const [mode, setMode] = useState('map');
  const [placeCurrent, setPlaceCurrent] = useState(null);
  const [placeFocused, setPlaceFocused] = useState(null);
  const [vibes, setVibes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [mapSettings, setMapSettings] = useState({
    theme: 'default'
  });
  const [viewport, setViewport] = useState({
    latitude: 47.6062,
    longitude: -122.3321,
    zoom: 14,
    bearing: 0,
    pitch: 0
  });
  const [selectedVibes, setSelectedVibes] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [mapKey, setMapKey] = useState(0);
  const [isMobileSize, setIsMobileSize] = useState(window.innerWidth < 1024);

  useEffect(() => {
    setIsLoading(true);

    Promise.all([
      apiFetch({ path: '/vibemap/v1/settings' }),
      apiFetch({ path: '/vibemap/v1/places-data' })
    ])
    .then(([settings, response]) => {
      if (settings?.center_latitude && settings?.center_longitude) {
        setViewport(prev => ({
          ...prev,
          latitude: parseFloat(settings.center_latitude),
          longitude: parseFloat(settings.center_longitude)
        }));
        
        setMapSettings(prev => ({
          ...prev,
          theme: settings.map_theme || 'default'
        }));

        if (settings?.location_slug) {
          fetch(`https://api.vibemap.com/v0.3/boundaries/?admin_level=both&slug=${settings.location_slug}`)
            .then(response => response.json())
            .then(data => {
              if (data?.results?.[0]?.the_geom?.coordinates?.[0]?.[0]) {
                setBoundary(data.results[0].the_geom.coordinates[0][0]);
              }
            })
            .catch(error => console.error('Error fetching boundary:', error));
        }
      }

      if (response?.places) {
        const transformedPlaces = transformPlaces(response.places);

        setPlaces(transformedPlaces);
        setVibes(response.vibes || []);
        setCategories(response.categories || []);
      }

      setIsLoading(false);
    })
    .catch(error => {
      console.error('Error loading data:', error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    setMapKey(prev => prev + 1);
  }, [forceMobileView]);

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 1024;
      setIsMobileSize(isMobile);
      if (isMobile) {
        setMode('map');
        setPlaceCurrent(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFilterChange = ({ vibes, categories }) => {
    setSelectedVibes(vibes || []);
    setSelectedCategories(categories || []);
  };

  const vibeChips = vibes.map(vibe => ({
    id: vibe.id.toString(),
    text: vibe.name,
    showClose: false,
    name: vibe.name,
    slug: vibe.slug  
  }));

  const categoryChips = categories.map(category => ({
    id: category.id.toString(),
    text: category.name,
    showClose: false,
    name: category.name,
    slug: category.slug  
  }));

  const handleCardClick = (place) => {
    setPlaceCurrent(place);
  };

  const handleCardFocus = (place) => {
    setPlaceFocused(place);
  };

  const mapConfig = {
    bearing: viewport.bearing,
    mapStyle: mapSettings.theme,
    markerStyle: 'images',
    showExplore: false,
    showEvents: false,
    showPlaces: true,
    map3d: false,
    scrollZoom: true
  };

  if (isLoading) {
    return <div>Loading map data...</div>;
  }

  return (
    <div style={{ height: `${height}px`, width: '100%' }}>
      <MapLayout
        key={mapKey}
        isMobile={forceMobileView || isMobileSize}
        isTablet={forceMobileView || isMobileSize}
        hasPlace={!!placeCurrent}
        places={places}
        placeCurrent={placeCurrent}
        placeFocused={placeFocused}
        setPlaceCurrent={setPlaceCurrent}
        setPlaceFocused={setPlaceFocused}
        mode={mode}
        setMode={setMode}
        vibes={vibeChips}
        categories={categoryChips}
        showFilters={true}
        selectedVibes={selectedVibes}
        selectedCategories={selectedCategories}
        onChange={handleFilterChange}
        themeObject={{}}
        showForPrint={false}
        showMapCards={true}
        cardStyle="list"
        handleModeToggle={() => setMode(mode === 'map' ? 'list' : 'map')}
        handleCardClick={handleCardClick}
        handleCardFocus={handleCardFocus}
        handleCardHover={() => {}}
        handleMarkerClick={handleCardClick}
        mapConfig={mapConfig}
        showLogo={true}
        placesLoading={isLoading}
        expandCollapse={true}
        showLeft={!isMobileSize && !forceMobileView}
        mapHeight={height}
        mapWidth="100%"
        hasLocation={true}
        showBoundary={showBoundary}
        boundaryCoordinates={boundary}
        showMarkers={true}
        headerHeight={0}
        mapLogoTopMargin={0}
        useMobileView={forceMobileView || isMobileSize}
        compact={false}
        showMap={true}
        showCards={true}
        viewport={viewport}
        scrollZoom={true}
        showHeat={true}
      />
    </div>
  );
};

function decodeHTMLEntities(text) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

function initializeMap() {
  const mapBlocks = document.querySelectorAll('.wp-block-vibemap-places-map-native');

  mapBlocks.forEach((element, index) => {
    try {
      const attributesJson = element.getAttribute('data-attributes');
      if (!attributesJson) {
        throw new Error('No attributes found on map element');
      }

      const attributes = JSON.parse(decodeHTMLEntities(attributesJson));
      element.innerHTML = '';
      const root = createRoot(element);
      root.render(<MapComponent attributes={attributes} />);
    } catch (error) {
      console.error(`Error initializing map block ${index + 1}:`, error);
      element.innerHTML = `<div class="error">Error initializing map: ${error.message}</div>`;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMap);
} else {
  initializeMap();
}

const transformPlaces = (rawPlaces) => {
  return rawPlaces
    .map(place => ({
      id: place.id,
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [
          parseFloat(place.meta?.vibemap_place_longitude) || 0,
          parseFloat(place.meta?.vibemap_place_latitude) || 0
        ]
      },
      properties: {
        name: place.title,
        description: place.content,
        image: place.featured_image,
        vibemap_images: place.meta?.vibemap_place_images ? JSON.parse(place.meta.vibemap_place_images) : [],
        latitude: parseFloat(place.meta?.vibemap_place_latitude) || 0,
        longitude: parseFloat(place.meta?.vibemap_place_longitude) || 0,
        categories: place.categories || [],
        vibes: place.vibes || [],
        score_combined: parseFloat(place.meta?.vibemap_place_score_combined || "0"),
        rating: parseFloat(place.meta?.vibemap_place_rating || "0"),
        aggregate_rating: parseFloat(place.meta?.vibemap_place_aggregate_rating || "0"),
        aggregate_rating_count: parseInt(place.meta?.vibemap_place_aggregate_rating_count || "0"),
        address: place.meta?.vibemap_place_address || "",
        city: place.meta?.vibemap_place_city || "",
        neighborhood: place.meta?.vibemap_place_neighborhood || "",
        short_description: place.meta?.vibemap_place_short_description || "",
        text_full: place.meta?.vibemap_place_text_full || "",
        url: place.meta?.vibemap_place_url || "",
        telephone: place.meta?.vibemap_place_telephone || "",
        twitter: place.meta?.vibemap_place_twitter || "",
        facebook: place.meta?.vibemap_place_facebook || "",
        instagram: place.meta?.vibemap_place_instagram || "",
        price: place.meta?.vibemap_place_price || ""
      }
    }))
    .filter(place => 
      place.geometry.coordinates[0] !== 0 && 
      place.geometry.coordinates[1] !== 0 &&
      !isNaN(place.geometry.coordinates[0]) &&
      !isNaN(place.geometry.coordinates[1])
    );
};
